import { takeEvery, put, call } from "redux-saga/effects";
import { types } from "../reducers/project";
import { types as authenticationTypes } from "../reducers/authentication";
import { ProjectService } from "../../services";
import { formatErrors } from "../../helpers/ErrorFormater";
import { MessageTypes } from "../../helpers/Messages";
import i18next from "i18next";
import { filterParams } from "../../helpers/SubjectSearch";

function* get({ value: { namespace, module, view } }) {
  try {
    const {
      data,
    } = yield call(() => ProjectService.get({ namespace, module, view }));
    yield put({
      type: types.PROJECT_SUCCESS, value: {
        projects: data.data,
        options: data.options
      }
    });
  } catch (e) {
    const formattedErrors = formatErrors(e)
    yield put({
      type: types.PROJECT_FAILED,
      value: formattedErrors,
    });
  }
}

function* getData({ value }) {
  try {
    const { data } = yield call(() => ProjectService.getData(value));
    if (data) {
      yield put({ type: types.PROJECT_SUCCESS, value: { [`${value.module}-${value.view}`]: data }, key: `${value.module}-${value.view}` });
    }
  } catch (e) {
    const formattedErrors = formatErrors(e)
    yield put({
      type: types.PROJECT_FAILED,
      value: formattedErrors,
    });
  }
}
function* getKyc({ id, userId, value }) {
  try {
    const { data } = yield call(() => ProjectService.getKyc(id, userId, value));
    if (data) {
      yield put({ type: types.PROJECT_KYC_SUCCESS, value: data });
    }
  } catch (e) {
    const formattedErrors = formatErrors(e)
    yield put({
      type: types.PROJECT_KYC_FAILED,
      value: formattedErrors,
    });
  }
}

function* submit({ value: { view, module, ...forms }, callback }) {
  try {
    const {
      data,
    } = yield call(() => {
      return ProjectService.submit({ view, module, ...forms }, module)
    });
    if (data) {
      yield put({ type: types.PROJECT_SUBMIT_SUCCESS, value: { view, ...data.data } });
      yield put({ type: authenticationTypes.AUTHENTICATION_SUCCEDDED, value: data.user });
      callback(
        MessageTypes.SUCCESS,
        i18next.t('Information submitted successfully'),
        data,
      );
    }
  } catch (e) {
    const formattedErrors = formatErrors(e)
    yield put({
      type: types.PROJECT_SUBMIT_FAILED,
      value: formattedErrors,
    });
  }
}

function* publicationsUpdate({ value, callback, onError }) {
  try {
    const {
      projectId,
      rowSelectionMode,
      rowSelectionExcluded,
      selectedAll,
      config,
      activeFiltersAll,
      // ...payload // all, entity, selected
    } = value;

    const selectedKeys = Object.keys(selectedAll);

    for (let i = 0; i < selectedKeys.length; i++) {
      const key = selectedKeys[i];
      const mode = rowSelectionMode[key];
      const excluded = rowSelectionExcluded[key];
      const entity = config.find((c) => c.state === key)?.entity;
      if (entity) {
        if (mode === 'all' || !mode) {
          if (selectedAll[key].length) {
            const activeFilters = activeFiltersAll[key];
            const { pagination, ...filteredParams } = activeFilters ? filterParams({ activeFilters }) : {};
            yield call(() => ProjectService.updatePublications(projectId, {
              entity,
              selected: selectedAll[key].map((s) => typeof s === 'string' ? s : s.dbId),
            }, filteredParams));
          }
        } else {
          const activeFilters = activeFiltersAll[key];
          const { pagination, ...filteredParams } = activeFilters ? filterParams({ activeFilters }) : {};
          yield call(() => ProjectService.updatePublications(projectId, {
            entity,
            all: true,
            excluded: excluded.map((s) => typeof s === 'string' ? s : s.dbId),
          }, filteredParams));
        }
      }
    }

    yield put({ type: types.PUBLICATIONS_UPDATE_SUCCESS });
    callback(
      MessageTypes.SUCCESS,
      i18next.t('Information submitted successfully')
    );
  } catch (e) {
    const formattedErrors = formatErrors(e)
    yield put({
      type: types.PUBLICATIONS_UPDATE_FAILED,
      value: formattedErrors,
    });
    if (typeof onError === 'function') {
      onError();
    }
  }
}

function* getInvitations() {
  try {
    const {
      data,
    } = yield call(() => ProjectService.getInvitedProjects());
    if (data) {
      yield put({ type: types.INVITATIONS_SUCCEDDED, value: data.data });
    }
  } catch (e) {
    const formattedErrors = formatErrors(e)
    yield put({
      type: types.INVITATIONS_FAILED,
      value: formattedErrors,
    });
  }
}

function* changeStatus({ value, callback }) {
  try {
    const { id, ...payload } = value;
    const {
      data,
    } = yield call(() => ProjectService.setProjectStatus(id, payload));
    if (data) {
      const { user, data: projects, options } = data;
      yield put({ type: authenticationTypes.AUTHENTICATION_SUCCEDDED, value: user });
      yield put({ type: types.PROJECT_SUCCESS, value: { projects, options } });
      callback(
        MessageTypes.SUCCESS,
        i18next.t('Information submitted successfully')
      );
    }
  } catch (e) {
    const formattedErrors = formatErrors(e)
    yield put({
      type: types.PROJECT_STATUS_FAILED,
      value: formattedErrors,
    });
  }
}

function* remove({ value, callback }) {
  try {
    const { data } = yield call(() => ProjectService.remove(value.id, value.data));
    if (data) {
      yield put({ type: types.PROJECT_REMOVE_SUCCESS, value: data });
      callback(MessageTypes.SUCCESS, i18next.t('Project deleted'));
    }
  } catch (e) {
    const formattedErrors = formatErrors(e);
    yield put({
      type: types.PROJECT_REMOVE_FAILED,
      value: formattedErrors,
    });
  }
}

function* getCaddReports({ value, callback, signal }) {
  try {
    const {
      data,
    } = yield call(() => ProjectService.getCaddReports(value, signal));
    yield put({ type: types.CADD_REPORT_SUCCESS, value: data });
  } catch (e) {
    const formattedErrors = formatErrors(e)
    yield put({
      type: types.CADD_REPORT_FAILED,
      value: formattedErrors,
    });
  }
  if (typeof callback === 'function') {
    callback();
  }
}

export default function* init() {
  yield takeEvery(
    types.PROJECT_REQUEST,
    get,
  );
  yield takeEvery(
    types.PROJECT_DATA,
    getData,
  );
  yield takeEvery(
    types.PROJECT_KYC,
    getKyc,
  );
  yield takeEvery(
    types.PROJECT_SUBMIT_REQUEST,
    submit,
  );
  yield takeEvery(
    types.PUBLICATIONS_UPDATE,
    publicationsUpdate,
  );
  yield takeEvery(
    types.INVITATIONS,
    getInvitations,
  );
  yield takeEvery(
    types.PROJECT_STATUS,
    changeStatus,
  );
  yield takeEvery(
    types.PROJECT_REMOVE,
    remove,
  );
  yield takeEvery(
    types.CADD_REPORT_REQUEST,
    getCaddReports,
  )
}
